import { EChartService } from './echart.service';
import { SchoolGroupTrendAnalysis, PsychologicalSafety } from '@/models/school-group/school-group-trend-analysis';
import i18n from '@/i18n';

export class TrendanalysisPsychologicalSafetyEchartService extends EChartService {
  self: any;
  val: number;
  data: PsychologicalSafety;
  medianLabels: string[];
  maxHeight: number;

  constructor() {
    super();
    this.series = [];
    this.labels = [];
    this.medianLabels = [];
    this.data;
    this.maxHeight = 1;
  }

  /**
   * AIGROW2-184 箱ひげ図にデータテーブルを追加
   * 箱ひげ図のみにデータを表形式で表示する
   * EChartService が箱ひげ図以外のグラフでも共通なので、空配列を返すだけのものを追加しています
   * subTableColmuns
   * subTableData
   */
  get subTableColmuns(): any[] {
    return [];
  }
  get subTableData(): any[] {
    return [];
  }

  get options() {
    return {
      title: { left: 'center', top: 10, text: i18n.t('PSYCHOLOGICAL_SAFETY').toString() },
      legend: {
        data: [...this.labels, ...this.medianLabels],
        top: 100,
        right: 0,
        orient: 'vertical',
      },
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          // 外れ値(未満)生徒名表示（最新）
          if (params.seriesName.indexOf(this.serialNumberPrefix) === 0 && params.value[0] <= -90 && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(params.seriesName, params.value[0], params.value[params.encode.y[0]], '', this.displayLatestOutlierLowerStudentNames);
          }
          // 外れ値(超過)生徒名表示（最新）
          if (params.seriesName.indexOf(this.serialNumberPrefix) === 0 && 90 <= params.value[0] && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(params.seriesName, params.value[0], params.value[params.encode.y[0]], '', this.displayLatestOutlierUpperStudentNames);
          }
          // 凡例表示（最新）
          if (params.seriesName.indexOf(this.serialNumberPrefix) === 0 && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipData(params.seriesName, params.value[0], params.value[params.encode.y[0]], this.displayLatestNomalStudentNames(params.value[0]));
          }
          // 中央値表示（最新）
          if (params.seriesName.indexOf(i18n.t('MEDIAN').toString()) === 0) {
            return `${params.seriesName}：${params.value[0]}`;
          }
          return;
        },
      },
      grid: { width: '80%', height: '80%', top: 'auto', left: 'auto' },
      xAxis: [
        {
          type: 'value',
          name: i18n.t('COMFORT_LEVEL').toString(),
          nameLocation: 'center',
          nameTextStyle: { fontWeight: 'bold' },
          nameGap: 30,
          min: -100,
          max: 100,
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: i18n.t('NUMBER_OF_PEOPLE').toString(),
          nameLocation: 'center',
          nameTextStyle: { fontWeight: 'bold' },
          nameGap: 30,
          min: 0,
          max: this.maxHeight,
          splitNumber: this.splitNumber,
        },
      ],
      series: this.series,
    };
  }

  addSeries(SchoolGroupTrendAnalysis: SchoolGroupTrendAnalysis): void {
    this.data = SchoolGroupTrendAnalysis.psychologicalSafety;
    const name: string = `SN-${this.labels.length + 1}`;
    const medianName: string = i18n.t('MEDIAN').toString() + `(${name})`;
    this.labels.push(name);
    this.medianLabels.push(medianName);
    const tmpHeight = this.yAxisMax;
    this.maxHeight = tmpHeight > this.maxHeight ? tmpHeight : this.maxHeight;

    // グラフデータ
    this.series.push({
      name: name,
      type: 'bar',
      barGap: '0%',
      data: this.latestDistributionChartData,
      barWidth: 10,
      z: 2,
    });
    // 中央値データ
    this.series.push({
      name: medianName,
      type: 'custom',
      renderItem: this.data.latestData.examDate ? this.medianRender : null,
      data: [{ value: [this.data.latestMedian * 50, this.yAxisMax] }],
      z: 4,
    });
  }

  medianRender(params: any, api: any) {
    // 開始位置の計算
    const countMax = api.value(1);
    const start = api.coord([api.value(0), countMax]);
    //中央値のグラフの幅
    const MEDIAN_BAR_WIDTH = 3;

    return {
      type: 'rect',
      shape: {
        x: start[0] - MEDIAN_BAR_WIDTH,
        y: 40,
        width: MEDIAN_BAR_WIDTH,
        height: api.getHeight() * 0.8,
      },
      style: api.style(),
    };
  }

  get yAxisMax(): number {
    if (this.data) {
      return Math.ceil(this.countMax / this.splitNumber) * this.splitNumber;
    }
    return 5;
  }

  get splitNumber(): number {
    // データの件数が5件未満の時にはその値に合わせた軸数にする
    if (this.data) {
      return this.countMax < 5 ? this.countMax : 5;
    }
    return 1;
  }

  get countMax(): number {
    const maxCountOfLatest = Math.max.apply(null, this.latestDistributionCountData);
    if (maxCountOfLatest === -Infinity) {
      //今回データがない場合、Math.max.applyは -Infinityを返す。そのときは暫定として1を返すことにする
      return 1;
    }
    return maxCountOfLatest;
  }

  // 今回の分布(Count)データ
  get latestDistributionCountData() {
    return this.data.latestData.trendData.map((d: any) => d.data[2]);
  }

  // 今回の分布(Chart)データ
  get latestDistributionChartData() {
    return this.data.latestData.trendData.map((d: any) => [d.data[0] + 5, d.data[2]]);
  }

  // 生徒データ（最新）
  displayLatestNomalStudentNames(normalData: number) {
    var comparisonLatestStudentData = this.data.latestData.trendData.filter((d: any) => {
      //d.data[0]、d.data[1]で範囲の指定しているので、指定をすることで人数を絞りこむ
      if (d.data[0] <= normalData && normalData <= d.data[1]) {
        return d.userName;
      }
    });
    var latestFilterUserName = comparisonLatestStudentData.map((d: any) => d.userName);
    //そのままだとスペース、カンマが入るためツールチップの表示がおかしくなるため
    return latestFilterUserName
      .join(',')
      .split(',')
      .join('<br>');
  }

  // 外れ値(未満)生徒データ（最新）
  get displayLatestOutlierLowerStudentNames(): string {
    return this.data.latestOutlierLowerStudents.map((student: any) => `${student.lastName} ${student.firstName}`).join('<br>');
  }

  // 外れ値(超過)生徒データ（最新）
  get displayLatestOutlierUpperStudentNames(): string {
    return this.data.latestOutlierUpperStudents.map((student: any) => `${student.lastName} ${student.firstName}`).join('<br>');
  }

  createTooltipData(labelName: string, xRangeLower: number, n: number, userName: any): string {
    // barChart表示用に+5しているので、区間を計算するために、調整を行っている
    return `${labelName}<br>` + i18n.t('RANGE').toString() + ` : ${xRangeLower - 4} 〜 ${xRangeLower + 5}<br>` + i18n.t('NUMBER_OF_PEOPLE').toString() + ` : ${n}<br>${userName}`;
  }

  createTooltipDataWithOutlierStudents(labelName: string, xRangeLower: number, n: number, userName: any, outlierUpperStudentNames: string): string {
    return this.createTooltipData(labelName, xRangeLower, n, userName) + `<br>` + i18n.t('OUTLIER_STUDENT').toString() + ` : <br>${outlierUpperStudentNames}`;
  }
}
